import React, { useState, useCallback, useRef, useEffect } from 'react';
import styled, { keyframes, css } from 'styled-components';
import {media} from "../../styles/breakpoints.js";
import {Highlight} from "../ui/PageElements.jsx";
import {CURSOR_INTERACTIVE} from "../../styles/cursors.js";

// Adjectives that describe the diverse work - will be shuffled during spin
const ADJECTIVES = [
    'C', 'C++', 'C♯', 'AUDIO', 'DSP', 'EMBEDDED', 'LOW-LEVEL', 'GAME',
    'SHADER', 'GRAPHICS', '3D', 'BACKEND', 'FRONTEND', 'UX', 'SOFTWARE',
    'INDIE', 'CURIOUS', 'VERSATILE', 'PLAYFUL'
];
const FINAL_ADJECTIVE = 'CREATIVE';

// Fisher-Yates shuffle
const shuffleArray = (array) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
};
// Animations
const glitchEffect = keyframes`
  0% { 
    transform: translate(-2px, 2px);
    opacity: 0.5;
  }
  50% { 
    transform: translate(2px, -2px);
    opacity: 0.7;
  }
  100% { 
    transform: translate(0px, 0px);
    opacity: 0.5;
  }
`;

// Styled components
const HeroSection = styled.section`
  min-height: 30vh;
  display: flex;
  align-items: center;
  position: relative;
  overflow: hidden;
    
    ${media.sm} {
        min-height: 60vh; // Reduce height on mobile
    }
`;

const HeroContent = styled.div`
  width: 100%;
  max-width: 800px;
  position: relative;
`;

const TechBadge = styled.div`
  display: inline-block;
  background-color: var(--background-color);
  color: var(--primary-color);
  padding: 5px 10px;
  font-size: 12px;
  letter-spacing: 1px;
  margin-bottom: 20px;
  text-transform: uppercase;
  position: relative;
  border: 1px solid var(--primary-color);
  
  &:before {
    content: "";
    position: absolute;
    top: -5px;
    left: -5px;
    width: calc(100% + 10px);
    height: calc(100% + 10px);
    border: 1px solid var(--primary-color);
    opacity: 0.5;
    z-index: -1;
    pointer-events: none;
  }
`;

const GlitchTitle = styled.h1`
  font-size: 64px;
  font-weight: 700;
  line-height: 1.1;
  margin-bottom: 30px;
  position: relative;
  display: inline-block;
  color: var(--text-color);
  text-shadow: 0 0 5px rgba(255, 51, 153, 0.5);
  white-space: nowrap;

  @media (max-width: 768px) {
    /* Fluid font: scales with viewport, min 24px, max 36px */
    /* Using 8vw to leave breathing room from screen edges */
    font-size: clamp(24px, 8vw, 36px);
  }
`;

// Glitch layer - positioned absolutely behind main text
const GlitchLayer = styled.span`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: -1;
  display: inline-block;
  pointer-events: none;
  white-space: nowrap;

  &.primary {
    color: var(--primary-color);
    animation: ${glitchEffect} 3s infinite alternate;
  }

  &.secondary {
    color: var(--secondary-color);
    animation: ${glitchEffect} 2s infinite alternate-reverse;
  }
`;

// Container for the adjective - fixed width to prevent layout shifts
const AdjectiveContainer = styled.span`
  display: inline-block;
  text-align: center;
  white-space: nowrap;
  /* Width will be set dynamically via style prop */
`;

// The actual adjective text - scales down if too long
const AdjectiveText = styled.span`
  cursor: ${CURSOR_INTERACTIVE};
  display: inline-block;
  white-space: nowrap;
  transition: color 0.1s ease, transform 0.05s ease;
  transform-origin: center center;

  &:hover {
    color: var(--primary-color);
  }

  ${props => props.$isSpinning && css`
    color: var(--primary-color);
  `}
`;

// Hidden span used to measure text width - must inherit font from parent
const MeasureSpan = styled.span`
  position: absolute;
  visibility: hidden;
  white-space: nowrap;
  pointer-events: none;
  /* Inherits font-size, font-weight, etc. from GlitchTitle parent */
`;

const HeroDescription = styled.p`
  font-size: 26px;
  max-width: 800px;
  margin-bottom: 40px;
  position: relative;

  @media (max-width: 768px) {
    font-size: clamp(18px, 5vw, 22px);
  }

  &:after {
    content: "";
    position: absolute;
    bottom: -15px;
    left: 0;
    width: 100px;
    height: 3px;
    background-color: var(--primary-color);
  }
`;

const StatsContainer = styled.div`
  display: flex;
  margin-top: 50px;
  gap: 40px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 20px;
  }
`;

const StatBox = styled.div`
  border: 1px solid var(--primary-color);
  padding: 15px;
  min-width: 150px;
  position: relative;
  
  &:before {
    content: "";
    position: absolute;
    top: 5px;
    left: 5px;
    width: calc(100% - 10px);
    height: calc(100% - 10px);
    border: 1px dashed var(--grid-color);
    pointer-events: none;
  }
`;

const StatTitle = styled.div`
  font-size: 12px;
  color: var(--primary-color);
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 5px;
`;

const StatValue = styled.div`
  font-size: 28px;
  font-weight: 700;
`;

const Hero = () => {
    const [currentAdjective, setCurrentAdjective] = useState(FINAL_ADJECTIVE);
    const [isSpinning, setIsSpinning] = useState(false);
    const [baseWidth, setBaseWidth] = useState(0);
    const [currentScale, setCurrentScale] = useState(1);

    const measureRef = useRef(null);
    const currentTextRef = useRef(null);

    // Measure "CREATIVE" width on mount and on resize (for responsive font-size)
    useEffect(() => {
        const measureWidth = () => {
            if (measureRef.current) {
                const width = measureRef.current.offsetWidth;
                setBaseWidth(width);
            }
        };

        measureWidth();
        window.addEventListener('resize', measureWidth);
        return () => window.removeEventListener('resize', measureWidth);
    }, []);

    // Calculate scale whenever adjective changes
    useEffect(() => {
        if (baseWidth > 0 && currentTextRef.current) {
            const currentWidth = currentTextRef.current.offsetWidth;
            if (currentWidth > baseWidth) {
                // Scale down to fit
                setCurrentScale(baseWidth / currentWidth);
            } else {
                setCurrentScale(1);
            }
        }
    }, [currentAdjective, baseWidth]);

    const startSlotMachine = useCallback(() => {
        if (isSpinning) return;

        setIsSpinning(true);

        // Shuffle adjectives and add CREATIVE at the end
        const shuffled = shuffleArray(ADJECTIVES);
        shuffled.push(FINAL_ADJECTIVE);

        // Calculate delays - start fast, slow down exponentially
        const totalItems = shuffled.length;
        let currentIndex = 0;
        let delay = 50; // Start very fast (50ms)

        const spin = () => {
            setCurrentAdjective(shuffled[currentIndex]);
            currentIndex++;

            if (currentIndex < totalItems) {
                // Exponential slowdown - more dramatic near the end
                const progress = currentIndex / totalItems;
                // Slow down more aggressively in the last third
                if (progress > 0.7) {
                    delay *= 1.35;
                } else if (progress > 0.5) {
                    delay *= 1.2;
                } else {
                    delay *= 1.1;
                }
                setTimeout(spin, delay);
            } else {
                // Animation complete
                setIsSpinning(false);
            }
        };

        spin();
    }, [isSpinning]);

    return (
        <HeroSection>
            <HeroContent>
                <TechBadge>SYSTEM INITIALIZED</TechBadge>
                <GlitchTitle aria-label="CREATIVE DEVELOPER">
                    {/* Hidden span to measure CREATIVE width - inside GlitchTitle to inherit font-size */}
                    <MeasureSpan ref={measureRef} aria-hidden="true">
                        {FINAL_ADJECTIVE}
                    </MeasureSpan>

                    {/* Glitch layers - same structure as main text for proper alignment */}
                    <GlitchLayer className="primary" aria-hidden="true">
                        <AdjectiveContainer style={{ width: baseWidth > 0 ? `${baseWidth}px` : 'auto' }}>
                            <span style={{ display: 'inline-block', transform: `scaleX(${currentScale})`, transformOrigin: 'center center' }}>
                                {currentAdjective}
                            </span>
                        </AdjectiveContainer>
                        <span> DEVELOPER</span>
                    </GlitchLayer>
                    <GlitchLayer className="secondary" aria-hidden="true">
                        <AdjectiveContainer style={{ width: baseWidth > 0 ? `${baseWidth}px` : 'auto' }}>
                            <span style={{ display: 'inline-block', transform: `scaleX(${currentScale})`, transformOrigin: 'center center' }}>
                                {currentAdjective}
                            </span>
                        </AdjectiveContainer>
                        <span> DEVELOPER</span>
                    </GlitchLayer>

                    {/* Main visible text */}
                    <AdjectiveContainer style={{ width: baseWidth > 0 ? `${baseWidth}px` : 'auto' }}>
                        <AdjectiveText
                            ref={currentTextRef}
                            $isSpinning={isSpinning}
                            onClick={startSlotMachine}
                            style={{ transform: `scaleX(${currentScale})` }}
                        >
                            {currentAdjective}
                        </AdjectiveText>
                    </AdjectiveContainer>
                    <span> DEVELOPER</span>
                </GlitchTitle>
                <HeroDescription>
                    Hi, I am Lucas Perron. I define myself as a <Highlight>'touche-à-tout'</Highlight>—a versatile maker driven by an insatiable curiosity. I love exploring the space where <Highlight>technical challenge</Highlight> meets <Highlight>creative expression</Highlight>. From the artful complexity of <Highlight>game development</Highlight> and audio synthesis to smart tools designed to change how we interact with information, I thrive on building <Highlight>unique experiences</Highlight>. Explore my work and see the kind of ideas I bring to life.
                </HeroDescription>

                {/*<StatsContainer>*/}
                {/*    <StatBox>*/}
                {/*        <StatTitle>Experience</StatTitle>*/}
                {/*        <StatValue>10+</StatValue>*/}
                {/*    </StatBox>*/}
                {/*    <StatBox>*/}
                {/*        <StatTitle>Projects</StatTitle>*/}
                {/*        <StatValue>37</StatValue>*/}
                {/*    </StatBox>*/}
                {/*    <StatBox>*/}
                {/*        <StatTitle>Technologies</StatTitle>*/}
                {/*        <StatValue>15+</StatValue>*/}
                {/*    </StatBox>*/}
                {/*</StatsContainer>*/}
            </HeroContent>
        </HeroSection>
    );
};

export default Hero;